// src/utils/instructorCapabilities.js
//
// Pass 56 (item H) — CANONICAL structured instructor capabilities, separate from qualification LEVEL.
// The legacy `member.labels.discipline` is free text ("Ski 2/3", "SB L1 & Ski L1", "Ski L1 / SB L3",
// "SB L2") which is NOT usable for assignment filtering or partner availability. This module defines
// the structured shape and a DETERMINISTIC, conservative interpreter: a discipline is only marked
// `true` when the text unambiguously names it; everything else stays UNKNOWN (never guessed false).
//
//   member.capabilities = { ski: true|false, snowboard: true|false, guiding: true|false }
//   member.capabilityLevels = { ski: 'L2', snowboard: 'L1', ... }   // optional, where known
//   member.capabilitiesSource = 'discipline_label_migration' | 'ops_entered' | ...
//
// A capability KEY that is absent/undefined = UNKNOWN (conservative). Consumers (assignment candidate
// filter, availability engine) must treat unknown as NOT-eligible for a discipline-specific need.

export const CAPABILITY_KEYS = ['ski', 'snowboard', 'guiding'];

// Pass 57B (item 11) — TRI-STATE capability semantics. UNKNOWN must never silently become NO: an
// unset key is UNKNOWN (conservative → not an assignment candidate, not counted as available), an
// explicit `false` is a deliberate NO, `true` is YES. The ops capability editor writes YES/NO/UNKNOWN.
export const CAPABILITY_STATE = { YES: 'yes', NO: 'no', UNKNOWN: 'unknown' };

// Read the tri-state for a member+key from the structured field (boolean true/false) or unknown.
export function capabilityState(member = {}, key) {
  const caps = (member.capabilities && typeof member.capabilities === 'object') ? member.capabilities : null;
  if (!caps || !(key in caps)) {
    // fall back to the deterministic label migration ONLY for a positive signal (never a NO)
    const migrated = resolveMemberCapabilities(member).capabilities;
    return migrated[key] === true ? CAPABILITY_STATE.YES : CAPABILITY_STATE.UNKNOWN;
  }
  if (caps[key] === true) return CAPABILITY_STATE.YES;
  if (caps[key] === false) return CAPABILITY_STATE.NO;
  return CAPABILITY_STATE.UNKNOWN;
}

// Build the Firestore patch to set ONE capability to YES/NO/UNKNOWN (UNKNOWN removes the key so it
// reverts to genuinely-unknown, never a silent NO). Pure — the caller applies via a gated writer.
export function setCapabilityPatch(member = {}, key, state) {
  const caps = { ...(member.capabilities && typeof member.capabilities === 'object' ? member.capabilities : {}) };
  if (state === CAPABILITY_STATE.YES) caps[key] = true;
  else if (state === CAPABILITY_STATE.NO) caps[key] = false;
  else delete caps[key]; // UNKNOWN → unset
  return { capabilities: caps, capabilitiesSource: 'ops_entered' };
}

// Deterministic parse of a free-text discipline label into structured capability booleans + levels.
// Returns { capabilities, capabilityLevels, matched } — capabilities only contains keys we can PROVE.
// Never sets a key to false from silence (absence = unknown). `matched` is false when nothing parsed.
export function parseDisciplineLabel(label = '') {
  const s = String(label || '').trim();
  const caps = {};
  const levels = {};
  if (!s) return { capabilities: caps, capabilityLevels: levels, matched: false };
  const lower = s.toLowerCase();

  // Ski: the word "ski" (not part of another word). SB / "snowboard" → snowboard.
  const skiMatch = /\bski\b/.test(lower);
  const sbMatch = /\bsb\b/.test(lower) || /snowboard/.test(lower);
  const guideMatch = /\bguid/.test(lower); // guide / guiding
  if (skiMatch) caps.ski = true;
  if (sbMatch) caps.snowboard = true;
  if (guideMatch) caps.guiding = true;

  // Levels: capture "L2"/"level 2"/"2/3" adjacent to a discipline where unambiguous.
  const skiLvl = lower.match(/ski[^a-z0-9]*(?:l|level)?\s*([0-9](?:\/[0-9])?)/);
  if (skiMatch && skiLvl) levels.ski = `L${skiLvl[1]}`;
  const sbLvl = lower.match(/(?:sb|snowboard)[^a-z0-9]*(?:l|level)?\s*([0-9](?:\/[0-9])?)/);
  if (sbMatch && sbLvl) levels.snowboard = `L${sbLvl[1]}`;

  return { capabilities: caps, capabilityLevels: levels, matched: Object.keys(caps).length > 0 };
}

// Resolve a member's effective capabilities: explicit structured field wins; else deterministic
// migration from labels.discipline; else empty (all unknown). Never fabricates.
export function resolveMemberCapabilities(member = {}) {
  if (member.capabilities && typeof member.capabilities === 'object') {
    return { capabilities: { ...member.capabilities }, capabilityLevels: member.capabilityLevels || {}, source: member.capabilitiesSource || 'explicit' };
  }
  const label = member.labels && member.labels.discipline;
  if (label) {
    const p = parseDisciplineLabel(label);
    if (p.matched) return { capabilities: p.capabilities, capabilityLevels: p.capabilityLevels, source: 'discipline_label_migration' };
  }
  return { capabilities: {}, capabilityLevels: {}, source: 'unknown' };
}

// Is a member a PROVEN candidate for a discipline? Conservative: unknown (key absent) → false.
export function hasCapability(member, key) {
  const { capabilities } = resolveMemberCapabilities(member);
  return capabilities[key] === true;
}

// Coverage report across a member set: how many have a KNOWN value for each capability key.
export function capabilityCoverage(members = []) {
  const known = { ski: 0, snowboard: 0, guiding: 0 };
  const anyKnown = [];
  for (const m of members) {
    const { capabilities, source } = resolveMemberCapabilities(m);
    let any = false;
    for (const k of CAPABILITY_KEYS) if (capabilities[k] === true) { known[k] += 1; any = true; }
    if (any || source === 'explicit') anyKnown.push(m.userId || m.id);
  }
  return { total: members.length, known, membersWithAnyKnownCapability: anyKnown.length, unknownMembers: members.length - anyKnown.length };
}

// Build the deterministic migration plan: for each member, the capabilities patch we WOULD write from
// their existing trusted label. Members with no parsable label are returned as `needsOpsInput` (never
// guessed). Pure — the caller applies the patch via a gated writer.
export function planCapabilityMigration(members = []) {
  const patches = [];
  const needsOpsInput = [];
  for (const m of members) {
    if (m.capabilities) continue; // already structured
    const label = m.labels && m.labels.discipline;
    const p = label ? parseDisciplineLabel(label) : { matched: false };
    if (p.matched) {
      patches.push({
        userId: m.userId, docId: m.id,
        set: { capabilities: p.capabilities, capabilityLevels: p.capabilityLevels, capabilitiesSource: 'discipline_label_migration' },
        fromLabel: label,
      });
    } else {
      needsOpsInput.push({ userId: m.userId, docId: m.id, name: m.name, reason: label ? 'unparsable_label' : 'no_discipline_data' });
    }
  }
  return { patches, needsOpsInput, counts: { migratable: patches.length, needsOpsInput: needsOpsInput.length } };
}
